import React from 'react';
import { Switch, Route, Redirect, RouteComponentProps } from 'react-router-dom';

// Pages & Forms
import ListVenuesPage from 'pages/private/admin/ListVenuesPage';
import StockCatalogPage from 'pages/private/stock/StockCatalogPage';
import StockModuleHomePage from 'pages/private/stock/StockModuleHomePage';
import StockProfitabilityPage from 'pages/private/stock/StockProfitabilityPage';
import StockHoldingGainLossPage from 'pages/private/stock/StockHoldingGainLossPage';

// Global Products
import ListStockGlobalProductsPage from 'pages/private/stock/ListStockGlobalProductsPage';
import CreateStockGlobalProductPage from 'pages/private/stock/CreateStockGlobalProductPage';
import EditStockGlobalProductPage from 'pages/private/stock/EditStockGlobalProductPage';

// Receptions
import StockReceptionsPage from 'pages/private/stock/StockReceptionsPage';
import CreateStockReceptionPage from 'pages/private/admin/CreateStockReceptionPage';
import EditStockReceptionPage from 'pages/private/stock/EditStockReceptionPage';
import ViewStockReceptionPage from 'pages/private/stock/ViewStockReceptionPage';

// Movements
import StockMovementsPage from 'pages/private/stock/StockMovementsPage';
import ViewStockMovementPage from 'pages/private/stock/ViewStockMovementPage';
import StockTransferForm from 'forms/StockTransferForm';
import StockAdjustmentForm from 'forms/StockAdjustmentForm';
import StockLossForm from 'forms/StockLossForm';

// Recipes
import StockRecipesPage from 'pages/private/stock/StockRecipesPage';
import EditStockRecipePage from 'pages/private/stock/EditStockRecipePage';
import StockRecipeForm from 'forms/StockRecipeForm';

// Productions
import ListStockProductionsPage from 'pages/private/stock/ListStockProductionsPage';
import StockProductionForm from 'forms/StockProductionForm';

// Suppliers
import StockSuppliersPage from 'pages/private/stock/StockSuppliersPage';
import EditStockSupplierPage from 'pages/private/stock/EditStockSupplierPage';
import StockSupplierForm from 'forms/StockSupplierForm';

// Warehouses
import StockWarehouseListPage from 'pages/private/stock/StockWarehouseListPage';
import EditStockWarehousePage from 'pages/private/stock/EditStockWarehousePage';
import StockWarehouseForm from 'forms/StockWarehouseForm';

// Production Centers
import StockProductionCenterListPage from 'pages/private/stock/StockProductionCenterListPage';
import EditStockProductionCenterPage from 'pages/private/stock/EditStockProductionCenterPage';
import StockProductionCenterForm from 'forms/StockProductionCenterForm';

// Context & Types
import EditStockModuleContext from 'contexts/EditStockModuleContext';
import {
  StockProductionCenter,
  StockReception,
  StockSupplier,
  StockWarehouse,
  GlobalProduct,
} from 'types/models/StockReceipt';
import { StockMovement, StockRecipe } from 'types/models/StockMovement';
import AppContext from 'contexts/AppContext';
import UserRoleType from 'types/enums/UserRoleType';
import useSetupInitialState from 'hooks/useSetupInitialState';

export default ({ match }: RouteComponentProps): JSX.Element | null => {
  const { authData } = React.useContext(AppContext);
  const entity = useSetupInitialState();
  const [selectedReception, setSelectedReception] = React.useState<StockReception | null>(null);
  const [selectedMovement, setSelectedMovement] = React.useState<StockMovement | null>(null);
  const [selectedRecipe, setSelectedRecipe] = React.useState<StockRecipe | null>(null);
  const [selectedSupplier, setSelectedSupplier] = React.useState<StockSupplier | null>(null);
  const [selectedProductionCenter, setSelectedProductionCenter] = React.useState<StockProductionCenter | null>(null);
  const [selectedWarehouse, setSelectedWarehouse] = React.useState<StockWarehouse | null>(null);
  const [selectedGlobalProduct, setSelectedGlobalProduct] = React.useState<GlobalProduct | null>(null);

  const role = authData?.user?.role;
  const isVenueEditor = role === UserRoleType.VenueEditor;
  const allowedRoles = [UserRoleType.Admin, UserRoleType.WarehouseEditor];
  const isAllowedByRole = Boolean(authData?.user) && allowedRoles.includes(role as UserRoleType);

  if (isVenueEditor) {
    if (!entity) {
      return null;
    }
    if (!('usesStockModule' in entity) || !entity.usesStockModule) {
      return <Redirect to="/private/welcome" />;
    }
  } else if (!isAllowedByRole) {
    return <Redirect to="/private/welcome" />;
  }

  return (
    <EditStockModuleContext.Provider
      value={{
        selectedReception,
        setSelectedReception,
        selectedMovement,
        setSelectedMovement,
        selectedRecipe,
        setSelectedRecipe,
        selectedSupplier,
        setSelectedSupplier,
        selectedProductionCenter,
        setSelectedProductionCenter,
        selectedWarehouse,
        setSelectedWarehouse,
        selectedGlobalProduct,
        setSelectedGlobalProduct,
      }}
    >
      <Switch>
        {/* Base */}
        <Route exact path={`${match.path}`} component={ListVenuesPage} />
        <Route path={`${match.path}/home`} component={StockModuleHomePage} />
        <Route path={`${match.path}/catalog`} component={StockCatalogPage} />
        <Route path={`${match.path}/profitability`} component={StockProfitabilityPage} />
        <Route path={`${match.path}/holding-gain-loss`} component={StockHoldingGainLossPage} />

        {/* Global Products */}
        <Route path={`${match.path}/global-products/create`} component={CreateStockGlobalProductPage} />
        <Route path={`${match.path}/global-products/:globalProductId/edit`} component={EditStockGlobalProductPage} />
        <Route path={`${match.path}/global-products`} component={ListStockGlobalProductsPage} />

        {/* Receptions */}
        <Route path={`${match.path}/receptions/create`} component={CreateStockReceptionPage} />
        <Route path={`${match.path}/receptions/:receptionId/edit`} component={EditStockReceptionPage} />
        <Route path={`${match.path}/receptions/:receptionId/view`} component={ViewStockReceptionPage} />
        <Route path={`${match.path}/receptions`} component={StockReceptionsPage} />

        {/* Movements */}
        <Route path={`${match.path}/movements/loss/create`} component={StockLossForm} />
        <Route path={`${match.path}/movements/adjustment/create`} component={StockAdjustmentForm} />
        <Route path={`${match.path}/movements/transfer/create`} component={StockTransferForm} />
        <Route path={`${match.path}/movements/:movementId/view`} component={ViewStockMovementPage} />
        <Route path={`${match.path}/movements`} component={StockMovementsPage} />

        {/* Recipes */}
        <Route path={`${match.path}/recipes/create`} component={StockRecipeForm} />
        <Route path={`${match.path}/recipes/:recipeId/edit`} component={EditStockRecipePage} />
        <Route path={`${match.path}/recipes`} component={StockRecipesPage} />

        {/* Productions */}
        <Route path={`${match.path}/productions/create`} component={StockProductionForm} />
        <Route path={`${match.path}/productions`} component={ListStockProductionsPage} />

        {/* Suppliers */}
        <Route path={`${match.path}/suppliers/create`} component={StockSupplierForm} />
        <Route path={`${match.path}/suppliers/:supplierId/edit`} component={EditStockSupplierPage} />
        <Route path={`${match.path}/suppliers`} component={StockSuppliersPage} />

        {/* Warehouses */}
        <Route path={`${match.path}/warehouses/create`} component={StockWarehouseForm} />
        <Route path={`${match.path}/warehouses/:warehouseId/edit`} component={EditStockWarehousePage} />
        <Route path={`${match.path}/warehouses`} component={StockWarehouseListPage} />

        {/* Production Centers */}
        <Route path={`${match.path}/production-centers/create`} component={StockProductionCenterForm} />
        <Route
          path={`${match.path}/production-centers/:productionCenterId/edit`}
          component={EditStockProductionCenterPage}
        />
        <Route path={`${match.path}/production-centers`} component={StockProductionCenterListPage} />

        {/* Fallback */}
        <Redirect to="/private/welcome" />
      </Switch>
    </EditStockModuleContext.Provider>
  );
};
